<template>
  <div class="container section-p70">
    <div class="row mb-3">
      <div class="col-auto">
        <h2 class="title-blue">{{ t("STATISTICS") }}</h2>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <table class="table table-striped table-borderless stats-table">
          <tbody>
          <tr v-for="stats of statsList" :key="stats.name">
            <th>{{ t(stats.title) }}</th>
            <td>
              <Button
                  :label="'XLSX'"
                  icon="pi pi-download"
                  class="p-button-icon-left"
                  @click="openXlsxDownloadModal(stats.name, t(stats.title), stats.extraFilters)"
              />
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>

    <Dialog
        v-model:visible="modalVisible"
        :header="modalTitle"
        modal
        class="statistics-download-dialog"
        :draggable="false"
        :dismissable-mask="true"
    >
      <form ref="formRef" @submit.prevent="downloadStatsAsXlsx">
        <Card class="rounded">
          <template #content>
            <div class="row">
              <div class="col">
                <Checkbox input-id="request-specific-date" v-model="requestMadeOnSpecificDate" :binary="true"/>
                <label for="request-specific-date" class="ms-2">{{ t("REQUEST_MADE_ON_SPECIFIC_DATE") }}</label>
              </div>
            </div>

            <div v-if="requestMadeOnSpecificDate" class="row mt-3">
              <div class="col-lg-6 mb-2">
                <label for="id-search-date" class="form-label">{{ t("DATE") }}</label>
                <DatePicker
                    input-id="id-search-date"
                    v-model="selectedDate"
                    date-format="dd.mm.yy"
                    required
                    class="w-100"
                />
              </div>

              <div class="col-lg-6 mb-2">
                <label for="id-search-time" class="form-label">{{ t("TIME") }}</label>
                <DatePicker
                    id="id-search-time"
                    v-model="selectedTime"
                    timeOnly
                    showIcon
                    required
                    class="w-100"
                />
              </div>
            </div>
          </template>
        </Card>

        <div v-if="extraFiltersEnabled" v-show="showExtraFilters" class="mt-3 pt-3 border-top">
          <div class="row mb-2">
            <div class="col">
              <label class="form-label" for="id-statistics-sex">{{ t("SEX") }}</label>
              <Select
                  v-model="selectedIsMale"
                  :options="sexOptions"
                  label-id="id-statistics-sex"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('SELECT')"
                  class="w-100"
                  showClear
              />
            </div>
          </div>

          <div class="row mb-2">
            <div class="col">
              <label class="form-label" for="age-range-start-0">{{ t("AGE") }}</label>
              <div class="row">
                <div class="col">
                  <Button :label="t('ADD_RANGE')" @click="addAgeRange" class="p-button-outlined"/>
                </div>
              </div>

              <div v-for="(ageRange, index) of selectedAgeRanges" :key="index" class="row align-items-center mt-2">
                <div class="col-sm-5 col-lg-6 pe-0">
                  <InputNumber :showButtons="true" v-model="ageRange[0]" :min="0" :max="ageRange[1]"
                               :input-id="`age-range-start-${index}`"
                               :placeholder="t('FROM')" required inputClass="w-100" class="d-block" @invalid="setAgeInputValidity"/>
                </div>

                <div class="col-sm-7 col-lg-6 ps-0 d-flex">
                  <span class="px-2 align-self-center">-</span>

                  <InputNumber :showButtons="true" v-model="ageRange[1]" :min="ageRange[0]" :max="150"
                               :input-id="`age-range-end-${index}`"
                               :placeholder="t('TO')" required inputClass="w-100" class="d-block" @invalid="setAgeInputValidity"/>

                  <Button
                      v-tooltip="t('REMOVE')"
                      icon="pi pi-times"
                      severity="danger"
                      text
                      rounded
                      class="ms-2"
                      @click="removeAgeRange(index)"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="row mb-2">
            <div class="col">
              <label for="search-id-citizenship" class="form-label">{{ t("CITIZENSHIP") }}</label>
              <MultiSelect
                  input-id="search-id-citizenship"
                  v-model="selectedCitizenships"
                  :options="citizenshipOptions"
                  optionLabel="text"
                  optionValue="value"
                  :placeholder="`${t('SEARCH')}...`"
                  :showToggleAll="false"
                  filter
                  class="w-100"
              />
            </div>
          </div>

          <div class="row mb-2">
            <div class="col">
              <label for="search-id-education" class="form-label">{{ t("EDUCATION") }}</label>
              <MultiSelect
                  input-id="search-id-education"
                  v-model="selectedEducations"
                  :options="educationOptions"
                  optionLabel="text"
                  optionValue="value"
                  :placeholder="`${t('SEARCH')}...`"
                  :showToggleAll="false"
                  filter
                  class="w-100"
              />
            </div>
          </div>

          <div class="row mb-2">
            <div class="col">
              <label for="search-id-training-status" class="form-label">{{ t("TRAINING_STATE_TITLE") }}</label>
              <MultiSelect
                  input-id="search-id-training-status"
                  v-model="selectedTrainingStatuses"
                  :options="trainingStatusOptions"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('SELECT')"
                  :showToggleAll="false"
                  filter
                  class="w-100"
              />
            </div>
          </div>

          <div v-if="selectedTrainingStatuses.length" class="row mb-2">
            <div class="col">
              <label class="form-label" for="training-date-range-start">{{ t("TRAINING_DATE_RANGE") }}</label>
              <div class="row">
                <div class="col-sm-5 col-lg-6 pe-0">
                  <DatePicker
                      input-id="training-date-range-start"
                      v-model="selectedTrainingStartDate"
                      :max-date="selectedTrainingEndDate"
                      :placeholder="t('FROM')"
                      date-format="dd.mm.yy"
                      class="w-100"
                  />
                </div>

                <div class="col-sm-7 col-lg-6 ps-0 d-flex">
                  <span class="px-2 align-self-center">-</span>

                  <DatePicker
                      input-id="training-date-range-end"
                      v-model="selectedTrainingEndDate"
                      :min-date="selectedTrainingStartDate"
                      :placeholder="t('TO')"
                      date-format="dd.mm.yy"
                      class="w-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      <Button
          v-if="extraFiltersEnabled"
          :label="t('MORE_FILTERS')"
          class="mt-3 w-100 p-3"
          :severity="showExtraFilters ? 'primary' : 'secondary'"
          @click="toggleExtraFilters"
      >
        <span class="me-2">{{ t("MORE_FILTERS") }}</span>
        <span class="pi pi-chevron-up" v-if="showExtraFilters"/>
        <span class="pi pi-chevron-down" v-else/>
      </Button>

      <template #footer>
        <Button :label="t('CANCEL')" severity="secondary" @click="modalVisible = false"/>
        <Button :label="t('DOWNLOAD')" :disabled="!selectedDate || !selectedTime || loading" @click="downloadStatsAsXlsx"/>
      </template>
    </Dialog>

    <file-downloader ref="fileDownloaderRef"/>
  </div>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {computed, ref, watch} from "vue";
import {injectAxios} from "@/util/inject";
import moment from "moment";
import {fileNameWithDateTime} from "@/util/fileUtil";
import FileDownloader from "@/components/FileDownloader.vue";
import {getEducationOptions} from "@/model/education";
import {citizenships} from "@/model/citizenship";
import {useToast} from "@/util/toastUtils";
import type {ComponentExposed} from "vue-component-type-helpers";

const {t, locale} = useI18n();
const axios = injectAxios();
const toast = useToast();

const modalVisible = ref(false);
const formRef = ref<HTMLFormElement | null>(null);
const statsFileName = ref("");
const modalTitle = ref("");
const selectedDate = ref(new Date());
const selectedTime = ref(moment().startOf("day").toDate());
const selectedTrainingStartDate = ref<Date | null>(null);
const selectedTrainingEndDate = ref<Date | null>(null);
const selectedIsMale = ref<boolean | null>(null);
const selectedTrainingStatuses = ref<string[]>([]);
const selectedCitizenships = ref<string[]>([]);
const selectedEducations = ref<string[]>([]);
const selectedAgeRanges = ref<string[][]>([]);
const loading = ref(false);
const fileDownloaderRef = ref<ComponentExposed<typeof FileDownloader>>();
const requestMadeOnSpecificDate = ref(true);
const showExtraFilters = ref(false);
const extraFiltersEnabled = ref(false);

const statsList = [
  {name: "past-group-participants", title: "PAST_GROUP_PARTICIPANTS", extraFilters: true},
  {name: "past-group-successful-participants", title: "PAST_GROUP_PARTICIPANTS_SUCCESSFUL", extraFilters: true},
  {name: "running-group-participants", title: "RUNNING_GROUP_PARTICIPANTS", extraFilters: true},
  {name: "unstarted-group-participants", title: "UNSTARTED_GROUP_PARTICIPANTS", extraFilters: true},
  {name: "unstarted-public-groups", title: "UNSTARTED_PUBLIC_GROUPS", extraFilters: false},
];

const educationOptions = computed(() => getEducationOptions(t));
const citizenshipOptions = computed(() => citizenships.map(c => ({value: c, text: t(`COUNTRY_CODES.${c}`)})));

const sexOptions = [
  {label: t("MEN"), value: true},
  {label: t("WOMEN"), value: false},
];

const trainingStatusOptions = [
  {label: t("TRAINING_STATE.STARTED"), value: "STARTED"},
  {label: t("TRAINING_STATE.ENDED"), value: "ENDED"},
  {label: t("TRAINING_STATE.ONGOING"), value: "ONGOING"},
];

const openXlsxDownloadModal = (fileName: string, title: string, extraFilters: boolean) => {
  const now = moment();
  selectedDate.value = now.toDate();
  selectedTime.value = now.startOf("day").toDate();
  selectedIsMale.value = null;
  selectedTrainingStartDate.value = null;
  selectedTrainingEndDate.value = null;
  selectedTrainingStatuses.value = [];
  selectedCitizenships.value = [];
  selectedEducations.value = [];
  selectedAgeRanges.value = [];
  requestMadeOnSpecificDate.value = true;
  showExtraFilters.value = false;
  extraFiltersEnabled.value = extraFilters;
  statsFileName.value = fileName;
  modalTitle.value = title;
  modalVisible.value = true;
};

const setAgeInputValidity = (elem: Event) => {
  const target = (elem.target || elem.currentTarget) as HTMLInputElement;
  if (!target?.value) {
    target?.setCustomValidity(t("ERRORS.FIELD_IS_REQUIRED"));
    return;
  }
  const min = target.min ? Number.parseInt(target.min) : 0;
  const max = target.max ? Number.parseInt(target.max) : 150;
  const val = Number.parseInt(target.value);
  if (val < min) {
    target.setCustomValidity(t("MUST_BE_AT_LEAST", {min: String(min)}));
    return;
  }
  if (val > max) {
    target.setCustomValidity(t("MUST_BE_LESS_OR_EQUAL_THAN", {max: String(max)}));
    return;
  }
  target.setCustomValidity("");
};

const downloadStatsAsXlsx = async (ev: Event) => {
  ev.preventDefault();

  if (!statsFileName.value || !selectedDate.value || !selectedTime.value) {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  if (selectedTrainingStatuses.value.length && (!selectedTrainingStartDate.value || !selectedTrainingEndDate.value)) {
    toast.makeErrorToast(`${t("TRAINING_DATE_RANGE")}: ${t("ERRORS.FIELD_IS_REQUIRED").toLowerCase()}`);
    return;
  }

  if (formRef.value && !formRef.value.reportValidity()) {
    return;
  }

  const dateValue = moment(selectedDate.value);
  const timeValue = moment(selectedTime.value, "HH:mm:ss");

  dateValue.hours(timeValue.hours());
  dateValue.minutes(timeValue.minutes());
  dateValue.seconds(timeValue.seconds());
  dateValue.milliseconds(0);

  const params = {
    timestamp: dateValue.toISOString(true),
    isMale: selectedIsMale.value,
    ages: getAgeRangeSearchFilter(),
    educations: getEducationsSearchFilter(),
    citizenships: getCitizenshipSearchFilter(),
    trainingStatuses: selectedTrainingStatuses.value,
    trainingTimestampStart: selectedTrainingStatuses.value.length ? moment(selectedTrainingStartDate.value).startOf("day").toDate() : null,
    trainingTimestampEnd: selectedTrainingStatuses.value.length ? moment(selectedTrainingEndDate.value).endOf("day").toDate() : null,
    language: locale.value,
  };

  loading.value = true;

  try {
    const fileName = fileNameWithDateTime(statsFileName.value, dateValue, "xlsx");
    await fileDownloaderRef.value.downloadFile(`/stats/${statsFileName.value}/excel`, params, fileName);
  } finally {
    loading.value = false;
  }
};

const getEducationsSearchFilter = () => {
  return selectedEducations.value.length > 0 ? selectedEducations.value : null;
};

const getCitizenshipSearchFilter = () => {
  return selectedCitizenships.value.length > 0 ? selectedCitizenships.value : null;
};

const getAgeRangeSearchFilter = () => {
  const ages = [];

  for (const ageRange of selectedAgeRanges.value) {
    if (ageRange[0] && ageRange[1]) {
      const start = Number.parseInt(ageRange[0]);
      const end = Number.parseInt(ageRange[1]);

      for (let age = start; age <= end; age++) {
        if (!ages.includes(age)) {
          ages.push(age);
        }
      }
    }
  }

  return ages.length > 0 ? ages : null;
};

const toggleExtraFilters = () => {
  showExtraFilters.value = !showExtraFilters.value;
};

const addAgeRange = () => {
  selectedAgeRanges.value.push([null, null]);
};

const removeAgeRange = (index) => {
  selectedAgeRanges.value.splice(index, 1);
};

watch(requestMadeOnSpecificDate, (v) => {
  const now = moment();
  selectedDate.value = now.toDate();
  selectedTime.value = v ? now.startOf("day").toDate() : now.toDate()
});

defineExpose({
  modalVisible,
  formRef,
  fileDownloaderRef,
  modalTitle,
  selectedAgeRanges,
  selectedTrainingStatuses,
  selectedEducations,
  selectedCitizenships,
  selectedTrainingStartDate,
  selectedTrainingEndDate,
  toggleExtraFilters,
  addAgeRange,
  removeAgeRange,
  setAgeInputValidity,
  downloadStatsAsXlsx,
  openXlsxDownloadModal,
});
</script>

<style scoped lang="scss">
.stats-table {
  tr {
    & > * {
      vertical-align: middle;
    }

    & > td {
      text-align: right;
    }
  }
}
</style>
